<template>
  <div>
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h6 class="mb-0 text-muted">
        <span v-if="occurrences.length" class="badge badge-secondary mr-1">{{ occurrences.length }}</span>
        intercorrência(s) registrada(s)
      </h6>
      <button class="btn btn-sm btn-primary" @click="openModal()">
        <i class="fas fa-plus mr-1"></i> Nova Intercorrência
      </button>
    </div>

    <div>
      <div v-if="loading" class="text-center py-4">
        <i class="fas fa-spinner fa-spin fa-2x text-primary"></i>
      </div>
      <div v-else-if="occurrences.length === 0" class="text-center py-4 text-muted">
        <i class="fas fa-check-circle fa-2x mb-2 text-success"></i>
        <p class="mb-0">Nenhuma intercorrência registrada.</p>
      </div>

      <div v-else class="occ-list">
        <div
          v-for="occ in occurrences"
          :key="occ.id"
          class="occ-block"
        >
          <!-- Cabeçalho da intercorrência -->
          <div
            class="occ-header"
            @click="toggleExpand(occ.id)"
          >
            <div class="occ-header-left">
              <i :class="expanded[occ.id] ? 'fas fa-chevron-down' : 'fas fa-chevron-right'" class="occ-chevron"></i>
              <div>
                <span class="occ-title">{{ occ.title }}</span>
                <span v-if="occ.occurrence_type" class="badge badge-secondary ml-2">
                  {{ occ.occurrence_type.name }}
                </span>
              </div>
            </div>
            <div class="occ-header-right">
              <small v-if="occ.assigned_user" class="occ-user">
                <i class="fas fa-user mr-1"></i>{{ occ.assigned_user.name }}
              </small>
              <span :class="statusBadge(occ.status)">{{ statusLabel(occ.status) }}</span>
              <small class="occ-time-in-status" title="Tempo neste status">
                <i class="fas fa-clock mr-1"></i>{{ timeInStatus(occ) }}
              </small>
              <!-- Botão trocar status (Vue dropdown) -->
              <div class="occ-status-toggle" @click.stop>
                <button
                  class="btn btn-sm btn-outline-secondary"
                  @click="toggleStatusMenu(occ.id)"
                  title="Alterar status"
                >
                  <i class="fas fa-exchange-alt"></i>
                </button>
                <div v-show="statusMenuOpen === occ.id" class="occ-status-menu">
                  <a
                    v-for="s in statusOptions"
                    :key="s.value"
                    class="occ-status-item"
                    :class="{ 'occ-status-active': occ.status === s.value }"
                    href="#"
                    @click.prevent="changeStatus(occ, s.value); statusMenuOpen = null"
                  >
                    <i v-if="occ.status === s.value" class="fas fa-check mr-1 text-success"></i>
                    {{ s.label }}
                  </a>
                </div>
              </div>
              <button class="btn btn-sm btn-crud edit" title="Editar" @click.stop="openModal(occ)">
                <i class="fas fa-pencil-alt"></i>
              </button>
              <button class="btn btn-sm btn-crud delete" title="Excluir" @click.stop="destroy(occ)">
                <i class="far fa-trash-alt"></i>
              </button>
            </div>
          </div>

          <!-- Conteúdo expandido -->
          <div v-if="expanded[occ.id]" class="occ-body">
            <!-- Descrição -->
            <div class="occ-description ck-content" v-html="occ.description"></div>

            <!-- Arquivos -->
            <div v-if="occ.files && occ.files.length" class="occ-section">
              <div class="occ-section-title"><i class="fas fa-paperclip mr-1"></i>Anexos</div>
              <div v-for="f in occ.files" :key="f.id" class="occ-file-link">
                <a :href="fileUrl(f)" target="_blank">
                  <i class="fas fa-file mr-1"></i>{{ f.original_name || f.file.split('/').pop() }}
                </a>
              </div>
            </div>

            <!-- Histórico de status -->
            <div v-if="occ.status_histories && occ.status_histories.length" class="occ-history">
              <div class="occ-history-title"><i class="fas fa-history mr-1"></i>Histórico de Status</div>
              <div v-for="h in occ.status_histories" :key="h.id" class="occ-history-item">
                <span :class="statusBadge(h.status_novo)" style="font-size: 0.7rem">{{ statusLabel(h.status_novo) }}</span>
                <span class="occ-history-text">
                  por <strong>{{ h.user ? h.user.name : '—' }}</strong>
                  em {{ formatDate(h.created_at) }}
                  <span v-if="h.status_anterior" class="text-muted"> · era: {{ statusLabel(h.status_anterior) }}</span>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal Nova / Editar -->
    <div v-if="showModal" class="modal-backdrop-custom" @click.self="closeModal">
      <div class="modal-dialog-custom modal-lg-custom">
        <div class="card mb-0">
          <div class="card-header d-flex justify-content-between align-items-center">
            <h5 class="mb-0">{{ form.id ? 'Editar Intercorrência' : 'Nova Intercorrência' }}</h5>
            <button type="button" class="btn btn-sm btn-light" @click="closeModal">
              <i class="fas fa-times"></i>
            </button>
          </div>
          <div class="card-body">
            <div class="row">
              <div class="col-md-6">
                <div class="form-group">
                  <label>Tipo <span class="text-muted">(opcional)</span></label>
                  <v-select
                    v-model="form.occurrence_type_id"
                    :options="occurrenceTypes"
                    :reduce="t => t.id"
                    label="name"
                    placeholder="Selecione o tipo"
                  />
                </div>
              </div>
              <div class="col-md-6">
                <div class="form-group">
                  <label>Responsável <span class="text-muted">(opcional)</span></label>
                  <v-select
                    v-model="form.assigned_user_id"
                    :options="users"
                    :reduce="u => u.id"
                    label="name"
                    placeholder="Selecione o usuário"
                  />
                </div>
              </div>
              <div class="col-md-12">
                <div class="form-group">
                  <label>Título <span class="required">*</span></label>
                  <input v-model="form.title" type="text" class="form-control" placeholder="Título da intercorrência" />
                </div>
              </div>
              <div class="col-md-12">
                <div class="form-group">
                  <label>Descrição <span class="required">*</span></label>
                  <ckeditor v-model="form.description" :editor="editor" :config="editorConfig" />
                </div>
              </div>
              <div class="col-md-12">
                <div class="form-group">
                  <label>Anexos</label>
                  <input type="file" class="form-control" multiple @change="onFileChange" />
                  <ul v-if="form.newFiles.length" class="mt-1 pl-3 mb-0">
                    <li v-for="(f, i) in form.newFiles" :key="i" class="small">
                      {{ f.name }} ({{ (f.size / 1024).toFixed(1) }} KB)
                      <button type="button" class="btn btn-link p-0 ml-1" style="font-size: 0.8rem" @click="form.newFiles.splice(i, 1)">remover</button>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
          <div class="card-footer d-flex justify-content-end">
            <button class="btn btn-secondary mr-2" @click="closeModal">Cancelar</button>
            <button class="btn btn-primary" :disabled="saving" @click="save">
              <i v-if="saving" class="fas fa-spinner fa-spin mr-1"></i>
              Salvar
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';

class MyUploadAdapter {
  constructor(loader) { this.loader = loader; }
  upload() {
    if (!this.loader || !this.loader.file) return Promise.reject('No file to upload');
    return this.loader.file.then(file => {
      if (!file) return Promise.reject('Invalid file');
      return new Promise((resolve, reject) => {
        this._initRequest();
        this._initListeners(resolve, reject, file);
        this._sendRequest(file);
      });
    });
  }
  abort() { if (this.xhr) this.xhr.abort(); }
  _initRequest() {
    const xhr = (this.xhr = new XMLHttpRequest());
    xhr.open('POST', 'https://api.intranet.it4d.com.br/api/v1/images/upload', true);
    xhr.responseType = 'json';
  }
  _initListeners(resolve, reject, file) {
    const xhr = this.xhr;
    const loader = this.loader;
    xhr.addEventListener('error', () => reject(`Couldn't upload file: ${file.name}.`));
    xhr.addEventListener('abort', () => reject());
    xhr.addEventListener('load', () => {
      const response = xhr.response;
      if (!response || response.error) return reject(response?.error?.message || 'Upload error');
      resolve({ default: response.url });
    });
    if (xhr.upload && loader) {
      xhr.upload.addEventListener('progress', evt => {
        if (evt.lengthComputable) { loader.uploadTotal = evt.total; loader.uploaded = evt.loaded; }
      });
    }
  }
  _sendRequest(file) {
    const data = new FormData();
    data.append('upload', file);
    this.xhr.send(data);
  }
}

function MyUploadAdapterPlugin(editor) {
  editor.plugins.get('FileRepository').createUploadAdapter = loader => new MyUploadAdapter(loader);
}

export default {
  name: 'CallOccurrences',

  props: {
    callId: { type: [Number, String], required: true },
  },

  data() {
    return {
      occurrences: [],
      occurrenceTypes: [],
      users: [],
      loading: true,
      showModal: false,
      saving: false,
      expanded: {},
      statusMenuOpen: null,
      form: this.emptyForm(),
      editor: ClassicEditor,
      editorConfig: { extraPlugins: [MyUploadAdapterPlugin] },
      statusOptions: [
        { value: 'pendente', label: 'Pendente' },
        { value: 'em_atuacao', label: 'Em Atuação' },
        { value: 'finalizada', label: 'Finalizada' },
      ],
    };
  },

  mounted() {
    this.fetchOccurrences();
    this.fetchTypes();
    this.fetchUsers();
    document.addEventListener('click', this._closeStatusMenu);
  },

  beforeDestroy() {
    document.removeEventListener('click', this._closeStatusMenu);
  },

  methods: {
    _closeStatusMenu() {
      this.statusMenuOpen = null;
    },

    toggleStatusMenu(id) {
      this.statusMenuOpen = this.statusMenuOpen === id ? null : id;
    },

    emptyForm() {
      return { id: null, title: '', description: '', occurrence_type_id: null, assigned_user_id: null, newFiles: [] };
    },

    fetchOccurrences() {
      this.loading = true;
      axios
        .get(this.$store.state.api + `calls/${this.callId}/occurrences`)
        .then(res => { this.occurrences = res.data.data || res.data; })
        .catch(() => {})
        .finally(() => { this.loading = false; });
    },

    fetchTypes() {
      axios
        .get(this.$store.state.api + 'occurrence-types?active=true')
        .then(res => { this.occurrenceTypes = res.data.data || res.data; })
        .catch(() => {});
    },

    fetchUsers() {
      axios
        .get(this.$store.state.api + 'users?paginated=false')
        .then(res => { this.users = res.data.data || res.data; })
        .catch(() => {});
    },

    toggleExpand(id) {
      this.$set(this.expanded, id, !this.expanded[id]);
    },

    openModal(occ = null) {
      if (occ) {
        this.form = {
          id: occ.id,
          title: occ.title,
          description: occ.description,
          occurrence_type_id: occ.occurrence_type_id,
          assigned_user_id: occ.assigned_user_id,
          newFiles: [],
        };
      } else {
        this.form = this.emptyForm();
      }
      this.showModal = true;
    },

    closeModal() {
      this.showModal = false;
    },

    onFileChange(e) {
      Array.from(e.target.files).forEach(f => this.form.newFiles.push(f));
      e.target.value = '';
    },

    save() {
      if (!this.form.title.trim() || !this.form.description.trim()) {
        this.$message('Atenção', 'Título e descrição são obrigatórios.', 'warning');
        return;
      }
      this.saving = true;

      const fd = new FormData();
      fd.append('title', this.form.title);
      fd.append('description', this.form.description);
      if (this.form.occurrence_type_id) fd.append('occurrence_type_id', this.form.occurrence_type_id);
      if (this.form.assigned_user_id) fd.append('assigned_user_id', this.form.assigned_user_id);
      this.form.newFiles.forEach(f => fd.append('files[]', f));

      const req = this.form.id
        ? axios.post(this.$store.state.api + `occurrences/${this.form.id}?_method=PUT`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
        : axios.post(this.$store.state.api + `calls/${this.callId}/occurrences`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });

      req
        .then(() => {
          this.$message('Sucesso', 'Intercorrência salva!', 'success');
          this.closeModal();
          this.fetchOccurrences();
        })
        .catch(() => { this.$message('Erro', 'Não foi possível salvar.', 'error'); })
        .finally(() => { this.saving = false; });
    },

    changeStatus(occ, newStatus) {
      if (occ.status === newStatus) return;
      axios
        .post(this.$store.state.api + `occurrences/${occ.id}/change-status`, { status: newStatus })
        .then(res => {
          const idx = this.occurrences.findIndex(o => o.id === occ.id);
          if (idx !== -1) this.$set(this.occurrences, idx, res.data);
        })
        .catch(() => { this.$message('Erro', 'Não foi possível alterar o status.', 'error'); });
    },

    destroy(occ) {
      swal({
        title: 'Deseja excluir esta intercorrência?',
        type: 'warning',
        buttons: {
          cancel: { text: 'Não', value: 'cancel', className: 'btn-danger' },
          confirm: { text: 'Sim', value: 'confirm' },
        },
      }).then(v => {
        if (v === 'confirm') {
          axios
            .delete(this.$store.state.api + `occurrences/${occ.id}`)
            .then(() => { this.fetchOccurrences(); })
            .catch(() => { this.$message('Erro', 'Não foi possível excluir.', 'error'); });
        }
      });
    },

    fileUrl(f) {
      return this.$store.state.apiBase + `occurrence-files/${f.id}/download`;
    },

    timeInStatus(occ) {
      let since = occ.created_at;
      if (occ.status_histories && occ.status_histories.length) {
        since = occ.status_histories[occ.status_histories.length - 1].created_at;
      }
      const diff = Date.now() - new Date(since).getTime();
      const days  = Math.floor(diff / 86400000);
      const hours = Math.floor((diff % 86400000) / 3600000);
      const mins  = Math.floor((diff % 3600000) / 60000);
      if (days > 0)  return days + 'd ' + hours + 'h';
      if (hours > 0) return hours + 'h ' + mins + 'min';
      return mins + 'min';
    },

    statusLabel(s) {
      const map = { pendente: 'Pendente', em_atuacao: 'Em Atuação', finalizada: 'Finalizada' };
      return map[s] || s;
    },

    statusBadge(s) {
      const map = { pendente: 'badge badge-secondary', em_atuacao: 'badge badge-warning', finalizada: 'badge badge-success' };
      return map[s] || 'badge badge-secondary';
    },

    formatDate(d) {
      if (!d) return '—';
      return new Date(d).toLocaleString('pt-BR');
    },
  },
};
</script>

<style scoped>
/* ── lista de intercorrências ───────────────────────── */
.occ-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.occ-block {
  border: 1px solid #dee2e6;
  border-radius: 6px;
  background: #fff;
  color: #343a40 !important;
  position: relative;
}

.occ-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  background: #f8f9fa;
  cursor: pointer;
  gap: 8px;
  border-radius: 6px 6px 0 0;
}
.occ-header:hover { background: #eef0f3; }

.occ-header-left {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
}

.occ-header-right {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.occ-chevron { color: #6c757d; font-size: 0.8rem; }

.occ-title { font-weight: 600; color: #212529; }

.occ-user { color: #6c757d; white-space: nowrap; }

.occ-time-in-status {
  color: #6c757d;
  white-space: nowrap;
  font-size: 0.78rem;
}

/* ── conteúdo expandido ─────────────────────────────── */
.occ-body {
  padding: 14px 16px;
  background: #fff;
  border-top: 1px solid #dee2e6;
  color: #343a40 !important;
  border-radius: 0 0 6px 6px;
}

.occ-description {
  margin-bottom: 12px;
  color: #343a40;
  line-height: 1.6;
}

/* ── seção de anexos ────────────────────────────────── */
.occ-section {
  margin-bottom: 12px;
  padding: 10px 12px;
  background: #f8f9fa;
  border-radius: 4px;
  border-left: 3px solid #6c757d;
}
.occ-section-title {
  font-weight: 600;
  color: #495057;
  margin-bottom: 6px;
  font-size: 0.85rem;
}
.occ-file-link a { color: #007bff; font-size: 0.85rem; display: inline-block; margin-bottom: 2px; }

/* ── histórico de status ────────────────────────────── */
.occ-history {
  padding: 10px 12px;
  background: #eef2f7;
  border-radius: 4px;
  border-left: 3px solid #4a90d9;
}
.occ-history-title {
  font-weight: 600;
  color: #344054;
  margin-bottom: 8px;
  font-size: 0.85rem;
}
.occ-history-item {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
  font-size: 0.83rem;
}
.occ-history-item:last-child { margin-bottom: 0; }
.occ-history-text { color: #495057; }
.occ-history-text strong { color: #212529; }

/* ── dropdown de status (Vue puro) ─────────────────── */
.occ-status-toggle {
  position: relative;
}
.occ-status-menu {
  position: absolute;
  right: 0;
  top: calc(100% + 4px);
  z-index: 9999;
  background: #fff;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  box-shadow: 0 4px 16px rgba(0,0,0,0.15);
  min-width: 148px;
  overflow: hidden;
}
.occ-status-item {
  display: block;
  padding: 8px 14px;
  color: #343a40 !important;
  text-decoration: none;
  font-size: 0.875rem;
  white-space: nowrap;
}
.occ-status-item:hover { background: #f0f4f8; }
.occ-status-active { background: #e9ecef; font-weight: 600; }

/* ── sub-modal (Nova / Editar intercorrência) ───────── */
.modal-backdrop-custom {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1070;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}
.modal-lg-custom {
  width: 100%;
  max-width: 720px;
  max-height: 90vh;
  overflow-y: auto;
}
.modal-lg-custom .card {
  background: #fff !important;
  color: #343a40 !important;
}
.modal-lg-custom .card-header,
.modal-lg-custom .card-body,
.modal-lg-custom .card-footer {
  background: #fff !important;
  color: #343a40 !important;
}
.modal-lg-custom label,
.modal-lg-custom input,
.modal-lg-custom h5 {
  color: #343a40 !important;
}
</style>
